import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import defaultLogo from '../new_logo-removebg-preview.png';

const SITE_NAME = 'VisionExploit';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://visionexploit.com').replace(/\/$/, '');
const DEFAULT_DESCRIPTION =
  'VisionExploit guides students through university admissions, visas and scholarships abroad, with expert advisors who know the process inside out.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
// Exported so a page-specific jsonLd entity (e.g. a BlogPosting's
// publisher) can reference the site-wide Organization/WebSite nodes by
// @id instead of duplicating them — see buildSiteGraph() below.
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const absoluteUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Site-wide Organization + WebSite nodes, built once from the same
 * 'global' CMS content Navbar/Footer/AboutUs already share via
 * SiteContentContext — costs zero extra requests. Stamped with a stable
 * @id so any page-specific entity that needs to reference the org (e.g. a
 * BlogPosting's publisher) links to this node instead of duplicating it,
 * which is how every page stays at exactly one Organization/WebSite entity
 * even though this runs on every route.
 *
 * Only ever includes fields with real data behind them: no sameAs (the CMS
 * has no social-profile fields), no foundingDate/numberOfEmployees (no CMS
 * field, would be fabricated), no SearchAction on WebSite (no free-text
 * search exists yet — see SearchResults.js).
 */
function buildSiteGraph(globalContent) {
  const name = globalContent.company_name || SITE_NAME;
  const logo = absoluteUrl(globalContent.header_logo_url) || absoluteUrl(defaultLogo);

  const organization = {
    '@type': 'EducationalOrganization',
    '@id': ORGANIZATION_ID,
    name,
    url: SITE_URL,
    logo,
  };

  const phone = (globalContent.phone_1 || '').trim();
  const email = (globalContent.email || '').trim();
  if (phone || email) {
    organization.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      ...(phone && { telephone: phone }),
      ...(email && { email }),
    };
  }

  const address = (globalContent.address || '').trim();
  if (address) {
    organization.address = address;
  }

  const website = {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name,
    url: SITE_URL,
  };

  return [organization, website];
}

/**
 * Single source of truth for per-route <title>, <meta description>,
 * canonical link, Open Graph / Twitter tags, robots directive and optional
 * JSON-LD. Every prop is optional — omit what a given page doesn't have yet
 * (e.g. while a dynamic route's data is still loading) and this renders
 * sensible, never-blank, never-"undefined" defaults in the meantime.
 *
 * Site-wide keywords (admin-configurable at /admin-portal/seo) are layered
 * in as a bonus <meta name="keywords"> via SiteContentContext, which also
 * supplies the Organization/WebSite JSON-LD below — never blocks or delays
 * the title/description/canonical tags above, which render synchronously
 * from props regardless of whether that context has resolved yet.
 *
 * Must be rendered inside <SiteContentProvider> (it is, App.js wraps the
 * whole route tree in one) — this calls useSiteContent() unconditionally.
 */
const Seo = ({
  title,
  description,
  canonical,
  image,
  type = 'website',
  publishedTime,
  noindex = false,
  jsonLd,
  suffix = true,
}) => {
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  // suffix=false for routes whose title prop is already a complete, final
  // string (the homepage's positioning line, or a dynamic detail page's
  // "<Name> — Programs, Fees & Admission" built from real data of
  // unpredictable length) — appending " | VisionExploit" to those would
  // just as often blow the 60-char budget as not. Static pages default to
  // suffix=true since their short hand-written titles were written with
  // the suffix in mind.
  // Same 'global' CMS fetch Navbar/Footer/AboutUs already share via
  // SiteContentContext — reused here for both the keywords meta tag and the
  // site-wide Organization/WebSite JSON-LD below. Zero extra requests.
  const { globalContent } = useSiteContent();
  const keywords = globalContent.keywords || '';

  // CMS "Site title" / "Meta description" (SEO & Metadata admin page) only
  // back the *fallback* used when a route doesn't pass its own title/
  // description — every route with real content (Home, a university page, a
  // blog post) still wins with its own, more specific values. Previously
  // these two admin fields were saved but never read anywhere, so filling
  // them in from the CMS silently did nothing.
  const resolvedTitle = title
    ? (isHome || !suffix ? title : `${title} | ${SITE_NAME}`)
    : (globalContent.site_title || SITE_NAME);
  const resolvedDescription = description || globalContent.meta_description || DEFAULT_DESCRIPTION;
  const resolvedCanonical = absoluteUrl(canonical || pathname);
  const resolvedImage = absoluteUrl(image) || DEFAULT_IMAGE;

  // A noindex page (admin portal, /my-applications, thin duplicate views)
  // is never indexed, so Google never reads its structured data either —
  // skip building/serializing it rather than shipping dead weight.
  let jsonLdScript = null;
  if (!noindex) {
    const graph = [...buildSiteGraph(globalContent)];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) graph.push(...jsonLd);
      else graph.push(jsonLd);
    }
    jsonLdScript = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
  }

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={resolvedCanonical} />

      {noindex && <meta name="robots" content="noindex" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:url" content={resolvedCanonical} />
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {jsonLdScript && (
        <script type="application/ld+json">{jsonLdScript}</script>
      )}
    </Helmet>
  );
};

export default Seo;
